import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import palette from 'src/theme/palette';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Notes as NotesIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  Home as HomeIcon,
  MonetizationOn as PriceIcon,
  LocationCity as CityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as FeatureIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';

const LeadViewModal = ({ open, onClose, lead }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!lead) return null;

  const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120, mt: 0.5 }}>
        {icon}
        <Typography
          sx={{
            ml: 1,
            fontFamily: 'poppinsMedium',
            color: palette.text.secondary,
            fontSize: '14px'
          }}
        >
          {label}:
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: 'poppinsRegular',
          color: palette.text.primary,
          flex: 1,
          fontSize: '14px'
        }}
      >
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  // Render property details in view modal
  const renderPropertyDetails = () => {
    if (!lead.property_details) {
      return null;
    }

    const property = lead.property_details;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.text.secondary,
            mb: 2,
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <HomeIcon fontSize="small" />
          Property Details
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          {/* Property Image */}
          <Box
            sx={{
              width: { xs: '100%', sm: 120 },
              height: 120,
              borderRadius: 2,
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: palette.grey[200],
              border: `1px solid ${palette.grey[300]}`
            }}
          >
            <img
              src={property.main_image}
              alt={property.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: palette.grey[300]
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No Image
              </Typography>
            </Box>
          </Box>

          {/* Property Info */}
          <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 1
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '18px',
                  color: palette.text.primary,
                  lineHeight: 1.2
                }}
              >
                {property.title}
              </Typography>

              {/* Property Link Button */}
              {property.property_link && (
                <Button
                  component="a"
                  href={property.property_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    textTransform: 'none',
                    fontFamily: 'poppinsMedium',
                    fontSize: '12px',
                    minWidth: 'auto',
                    color: palette.primary.main,
                    border: `1px solid ${palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: palette.primary.light,
                      color: palette.primary.contrastText
                    }
                  }}
                >
                  View
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationIcon sx={{ fontSize: 16, color: palette.text.secondary, mr: 0.5 }} />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: palette.text.secondary,
                  lineHeight: 1.2
                }}
              >
                {property.address}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: palette.text.disabled,
                  lineHeight: 1.2
                }}
              >
                {property.city || 'N/A'}
                {property.state && `, ${property.state}`}
              </Typography>
            </Box>

            {/* Property Stats */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`$${property.price?.toLocaleString() || 'N/A'}`}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ fontFamily: 'poppinsMedium' }}
              />
              <Chip
                label={`${property.bedrooms || 0} Beds`}
                variant="outlined"
                size="small"
                sx={{ fontFamily: 'poppinsMedium' }}
              />
              <Chip
                label={`${property.bathrooms || 0} Baths`}
                variant="outlined"
                size="small"
                sx={{ fontFamily: 'poppinsMedium' }}
              />
              <Chip
                label={`${property.area_size?.toLocaleString() || 0} sq ft`}
                variant="outlined"
                size="small"
                sx={{ fontFamily: 'poppinsMedium' }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderValuationDetails = () => {
    if (!lead.valuation_tool) {
      return null;
    }

    const valuation = lead.valuation_tool;

    const conditionText = {
      excellent: 'Excellent',
      good: 'Good',
      average: 'Average',
      poor: 'Poor',
      needs_repair: 'Needs Repair'
    };

    const getConditionColor = (condition) => {
      switch (condition) {
        case 'excellent':
          return 'success';
        case 'good':
          return 'primary';
        case 'average':
          return 'warning';
        case 'poor':
          return 'error';
        case 'needs_repair':
          return 'error';
        default:
          return 'default';
      }
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.text.secondary,
            mb: 2,
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <TrendingUpIcon fontSize="small" />
          Valuation Request Details
        </Typography>

        <Card
          variant="outlined"
          sx={{
            border: `1px solid ${palette.grey[200]}`,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Property Type and Basic Info */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 2,
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: palette.grey[100],
                  border: `1px solid ${palette.grey[300]}`,
                  mr: 3
                }}
              >
                {valuation.property_type?.image ? (
                  <img
                    src={valuation.property_type.image}
                    alt={valuation.property_type.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: valuation.property_type?.image ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: palette.grey[300]
                  }}
                >
                  <HomeIcon sx={{ color: palette.text.disabled, fontSize: 32 }} />
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      fontSize: '18px',
                      color: palette.text.primary
                    }}
                  >
                    {valuation.address || 'Address not provided'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CategoryIcon sx={{ fontSize: 16, color: palette.text.secondary, mr: 0.5 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: palette.text.secondary
                    }}
                  >
                    {valuation.property_type?.name || 'Property type not specified'}
                  </Typography>
                </Box>

                {/* Property Stats Chips */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {valuation.bed_rooms && (
                    <Chip
                      icon={<HomeIcon />}
                      label={`${valuation.bed_rooms} Beds`}
                      variant="outlined"
                      size="small"
                      sx={{ fontFamily: 'poppinsMedium' }}
                    />
                  )}
                  {valuation.bath_rooms && (
                    <Chip
                      icon={<HomeIcon />}
                      label={`${valuation.bath_rooms} Baths`}
                      variant="outlined"
                      size="small"
                      sx={{ fontFamily: 'poppinsMedium' }}
                    />
                  )}
                  {valuation.area_size && (
                    <Chip
                      icon={<HomeIcon />}
                      label={`${valuation.area_size} ${valuation.area_unit_type?.name || 'units'}`}
                      variant="outlined"
                      size="small"
                      sx={{ fontFamily: 'poppinsMedium' }}
                    />
                  )}
                  {valuation.build_year && (
                    <Chip
                      icon={<CalendarIcon />}
                      label={`Built: ${valuation.build_year}`}
                      variant="outlined"
                      size="small"
                      sx={{ fontFamily: 'poppinsMedium' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Detailed Information Grid */}
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<LocationIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                  label="Address"
                  value={valuation.address}
                />

                <InfoRow
                  icon={<CityIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                  label="City"
                  value={valuation.city?.name || valuation.city}
                />

                <InfoRow
                  icon={<LocationIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                  label="State"
                  value={valuation.state?.name || valuation.state}
                />
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120, mt: 0.5 }}>
                    <HomeIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />
                    <Typography
                      sx={{
                        ml: 1,
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Condition:
                    </Typography>
                  </Box>
                  <Chip
                    label={conditionText[valuation.condition] || valuation.condition || 'N/A'}
                    color={getConditionColor(valuation.condition)}
                    size="small"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      fontSize: '12px',
                      height: '24px'
                    }}
                  />
                </Box>

                <InfoRow
                  icon={<ScheduleIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                  label="Build Year"
                  value={valuation.build_year}
                />

                {valuation.renovation_text && (
                  <InfoRow
                    icon={<HomeIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                    label="Renovations"
                    value={valuation.renovation_text}
                  />
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Render quiz details with perfect layout
  const renderQuizDetails = () => {
    if (!lead.quiz) {
      return null;
    }

    const quiz = lead.quiz;

    const planningToMoveText = {
      immediately: 'Immediately',
      '1month': 'Within 1 Month',
      '3month': 'Within 3 Months',
      '6month': 'Within 6 Months',
      '1year': 'Within 1 Year',
      exploring: 'Exploring Options'
    };

    const transactionTypeColor = quiz.type === 'buy' ? 'success' : 'warning';

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.text.secondary,
            mb: 3,
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <BusinessIcon fontSize="small" />
          Customer Preferences
        </Typography>

        <Grid container spacing={3}>
          {/* Left Column - Basic Preferences */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                border: `1px solid ${palette.grey[200]}`,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 2.5,
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <TrendingUpIcon fontSize="small" />
                  Basic Preferences
                </Typography>

                {/* Property Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <HomeIcon sx={{ color: palette.primary.main, mr: 1.5, fontSize: '20px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '13px'
                      }}
                    >
                      Property Type
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        color: palette.text.primary,
                        fontSize: '14px'
                      }}
                    >
                      {quiz.property_type_details?.name || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>

                {/* Transaction Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <CategoryIcon sx={{ color: palette.primary.main, mr: 1.5, fontSize: '20px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '13px'
                      }}
                    >
                      Transaction Type
                    </Typography>
                    <Chip
                      label={
                        quiz.type === 'buy'
                          ? 'Buy'
                          : quiz?.type === 'rent'
                          ? 'Rent'
                          : quiz?.type === 'Sell'
                          ? 'Sell'
                          : 'Not specified'
                      }
                      color={transactionTypeColor}
                      size="small"
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        fontSize: '12px',
                        height: '24px'
                      }}
                    />
                  </Box>
                </Box>

                {/* Price Range */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <PriceIcon sx={{ color: palette.primary.main, mr: 1.5, fontSize: '20px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '13px'
                      }}
                    >
                      Price Range
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        color: palette.text.primary,
                        fontSize: '14px'
                      }}
                    >
                      ${parseFloat(quiz.min_price || 0).toLocaleString()} - $
                      {parseFloat(quiz.max_price || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                {/* Preferred City */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <CityIcon sx={{ color: palette.primary.main, mr: 1.5, fontSize: '20px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '13px'
                      }}
                    >
                      Preferred City
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        color: palette.text.primary,
                        fontSize: '14px'
                      }}
                    >
                      {quiz.city?.name || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>

                {/* Moving Timeline */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ScheduleIcon sx={{ color: palette.primary.main, mr: 1.5, fontSize: '20px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '13px'
                      }}
                    >
                      Moving Timeline
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        color: palette.text.primary,
                        fontSize: '14px'
                      }}
                    >
                      {planningToMoveText[quiz.planing_to_move] || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Features */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                border: `1px solid ${palette.grey[200]}`,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 2.5,
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <FeatureIcon fontSize="small" />
                  Desired Features
                  {quiz.features && quiz.features.length > 0 && (
                    <Chip
                      label={`${quiz.features.length} selected`}
                      size="small"
                      color="primary"
                      sx={{ ml: 1, height: '20px', fontSize: '11px' }}
                    />
                  )}
                </Typography>

                {quiz.features && quiz.features.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {quiz.features.map((feature, index) => (
                      <Box
                        key={feature.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: palette.grey[50],
                          border: `1px solid ${palette.grey[200]}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: palette.grey[100],
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            color: palette.success.main,
                            mr: 1.5,
                            fontSize: '18px'
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: 'poppinsSemiBold',
                              color: palette.text.primary,
                              fontSize: '14px',
                              lineHeight: 1.2
                            }}
                          >
                            {feature.category_name}
                          </Typography>
                          {feature.category_name && (
                            <Typography
                              sx={{
                                fontFamily: 'poppinsRegular',
                                color: palette.text.secondary,
                                fontSize: '12px',
                                mt: 0.5
                              }}
                            >
                              {feature.name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4,
                      color: palette.text.disabled
                    }}
                  >
                    <FeatureIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography sx={{ fontFamily: 'poppinsMedium', fontSize: '14px' }}>
                      No features selected
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsRegular', fontSize: '12px', mt: 0.5 }}>
                      Customer didn't specify any preferred features
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1000px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2.5,
          position: 'relative',
          borderTopLeftRadius: isMobile ? 0 : 8,
          borderTopRightRadius: isMobile ? 0 : 8
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText,
            fontSize: '20px'
          }}
        >
          Lead Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: palette.secondary.contrastText,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 3 }}>
        {/* Basic Lead Information */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.text.secondary,
              mb: 2.5,
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.6
            }}
          >
            <PersonIcon fontSize="small" />
            Contact Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {/* Name */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140 }}>
                  <PersonIcon sx={{ color: palette.primary.main, fontSize: '20px', mr: 1.5 }} />
                  <Typography
                    sx={{
                      fontFamily: 'poppinsMedium',
                      color: palette.text.secondary,
                      fontSize: '14px',
                      minWidth: 100
                    }}
                  >
                    Full Name:
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '14px',
                    flex: 1
                  }}
                >
                  {lead.name || 'N/A'}
                </Typography>
              </Box>

              {/* Email */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140 }}>
                  <EmailIcon sx={{ color: palette.primary.main, fontSize: '20px', mr: 1.5 }} />
                  <Typography
                    sx={{
                      fontFamily: 'poppinsMedium',
                      color: palette.text.secondary,
                      fontSize: '14px',
                      minWidth: 100
                    }}
                  >
                    Email:
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '14px',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    flex: 1
                  }}
                >
                  {lead?.email || 'N/A'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Contact */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140 }}>
                  <PhoneIcon sx={{ color: palette.primary.main, fontSize: '20px', mr: 1.5 }} />
                  <Typography
                    sx={{
                      fontFamily: 'poppinsMedium',
                      color: palette.text.secondary,
                      fontSize: '14px',
                      minWidth: 100
                    }}
                  >
                    Contact:
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '14px',
                    flex: 1
                  }}
                >
                  {lead.contact || 'N/A'}
                </Typography>
              </Box>

              {/* Date */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140 }}>
                  <CalendarIcon sx={{ color: palette.primary.main, fontSize: '20px', mr: 1.5 }} />
                  <Typography
                    sx={{
                      fontFamily: 'poppinsMedium',
                      color: palette.text.secondary,
                      fontSize: '14px',
                      minWidth: 100
                    }}
                  >
                    Date:
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '14px',
                    flex: 1
                  }}
                >
                  {lead.date || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Only show divider if there's content after basic info */}
        {(lead.property_details || lead.quiz) && (
          <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />
        )}

        {/* Property Details Section - Only show if property exists */}
        {lead.property_details && renderPropertyDetails()}

        {/* Quiz Details Section - Only show if quiz exists */}
        {lead.quiz && renderQuizDetails()}
        {/* Valuation Details Section - Only show if valuation exists */}
        {lead.valuation_tool && renderValuationDetails()}

        {/* Quiz Details Section - Only show if quiz exists */}
        {lead.quiz && renderQuizDetails()}

        {/* Only show divider if there's content before lead type/status */}
        {(lead.property_details || lead.quiz) && (
          <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />
        )}

        {/* Lead Type and Status */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography
            sx={{ fontFamily: 'poppinsMedium', color: palette.text.secondary, fontSize: '14px' }}
          >
            Lead Information:
          </Typography>
          <Chip
            icon={<CategoryIcon />}
            label={`Type: ${lead.lead_type?.name || 'General'}`}
            variant="outlined"
            color="primary"
            sx={{ fontFamily: 'poppinsMedium' }}
          />
          <Chip
            label={`Status: ${lead.status || 'Inactive'}`}
            variant="filled"
            color={lead.status === 'active' ? 'success' : 'default'}
            sx={{ fontFamily: 'poppinsMedium' }}
          />
        </Box>

        {/* Notes Section - Only show if notes exist */}
        {lead.note && (
          <>
            <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotesIcon sx={{ color: palette.primary.main, mr: 1.5 }} />
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '16px'
                  }}
                >
                  Additional Notes
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: palette.grey[50],
                  borderRadius: 2,
                  border: `1px solid ${palette.grey[200]}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'poppinsRegular',
                    color: palette.text.primary,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px'
                  }}
                >
                  {lead.note}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />

        {/* Timestamps */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                color: palette.text.secondary,
                fontSize: '13px',
                mb: 0.5
              }}
            >
              Created At
            </Typography>
            <Typography
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary, fontSize: '14px' }}
            >
              {lead.created_at || 'N/A'}
            </Typography>
          </Box>
          {lead.updated_at && lead.updated_at !== lead.created_at && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'poppinsMedium',
                  color: palette.text.secondary,
                  fontSize: '13px',
                  mb: 0.5
                }}
              >
                Last Updated
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.text.primary,
                  fontSize: '14px'
                }}
              >
                {lead.updated_at}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.grey[200]}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            fontFamily: 'poppinsSemiBold',
            px: 3,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: palette.primary.dark,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Close Details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadViewModal;
